import { Link } from 'react-router-dom';
import LearnPage from './LearnPage';
import SeeItLive from './SeeItLive';
import AbbrTip from '@/components/AbbrTip';

const FAQS = [
  {
    q: 'What is the Rule of 40?',
    a: 'A one-line health check for any growth company: take its most recent annual revenue growth rate, add its profit margin, and ask whether the total is at least 40. A company at 40 or above is scaling efficiently — every point of growth is matched by enough profitability that it doesn\u2019t need to burn capital to keep growing. Below 30, the company is buying growth with money it isn\u2019t making. The metric was popularized by venture capitalist Brad Feld around 2015 and is now the standard efficiency check used by Bessemer Venture Partners and most public-market software analysts.',
  },
  {
    q: 'Why does DiviDrip show THREE variants?',
    a: 'Because each definition answers a different question. Rule of 40 (FCF) = Revenue Growth + (Free Cash Flow ÷ Revenue) — the SaaS-investor convention. Rule of 40 (Operating) = Revenue Growth + (Operating Income ÷ Revenue) — more forgiving for capex-heavy industrials where heavy property-and-equipment investment is structural. Rule of 40 (SBC-adjusted FCF) = Revenue Growth + ((Free Cash Flow − Stock-Based Compensation) ÷ Revenue) — the strictest of the three, because GAAP cash-flow statements add stock compensation back as a non-cash expense, which quietly flatters FCF at any company that pays its people in shares. Reading all three side by side is the fastest way to spot a "dilution leak" hiding behind a positive-FCF headline.',
  },
  {
    q: 'What does it mean when FCF passes but Operating fails?',
    a: 'It means the company is generating real cash but reporting GAAP operating losses. Almost always, the gap is heavy stock-based compensation (SBC). SBC is a non-cash expense — it shows up as an operating-margin headwind but never leaves the bank account, so it doesn\u2019t hurt FCF. CrowdStrike is the textbook live example on DiviDrip right now: Rule of 40 (FCF) reads around 49 (a clean pass), but Rule of 40 (Operating) reads around 16 (a failure). The 33-point gap is almost entirely the SBC line — and the SBC-adjusted FCF variant proves it: subtract CrowdStrike\u2019s SEC-filed stock compensation (about 23% of revenue) from its cash flow and the score drops to roughly 26. That is the number existing shareholders actually experience, because future revenue is being diluted by stock grants.',
  },
  {
    q: 'And when Operating passes but FCF fails?',
    a: 'You\u2019re usually looking at a capex-heavy business. Amazon is the live example: Rule of 40 (Operating) reads around 24, but Rule of 40 (FCF) reads only 13 because of huge AWS data-center and fulfillment-network spending. That isn\u2019t inefficiency — it\u2019s capacity investment that should turn into future cash flow. For these names, the Operating variant is the truer read on the underlying business, and the FCF variant tells you how much capital is currently being plowed into expansion.',
  },
  {
    q: 'Where does the stock-based compensation number come from?',
    a: 'From the company\u2019s own annual SEC filing. DiviDrip reads the ShareBasedCompensation fact straight out of the 10-K (or 20-F / 40-F for foreign filers) via SEC XBRL, matches it to the same fiscal year as the revenue and cash-flow figures, and refreshes it on the weekly equity-quality run. If a company does not report that line, the SBC-adjusted tile simply does not appear — we never estimate it.',
  },
  {
    q: 'Why does the Rule of 40 sometimes say "Not meaningful"?',
    a: 'Because the math breaks on a tiny or lumpy revenue base. A biotech that booked $7M last year and a $220M licensing payment this year shows 3,000% "growth" — mathematically true, economically meaningless. DiviDrip suppresses all three tiles and prints the reason whenever either year\u2019s revenue is under $5M or revenue jumped more than 4× year-over-year, and the header verdict is capped at WATCH for those names. A number in the thousands should always make you suspicious, not excited.',
  },
  {
    q: 'What\u2019s a "good" Rule of 40 score?',
    a: 'Tiers: 60+ = Elite (very rare — only dominant compounders sustain this for multi-year stretches). 40–60 = Healthy, the canonical pass. 30–40 = Borderline — growth or margin is starting to crack, watch quarter-over-quarter. 0–30 = Failing — the company is funding growth with cash it isn\u2019t earning. Negative = burning cash with contracting revenue, an existential signal in a high-interest-rate environment.',
  },
  {
    q: 'Why doesn\u2019t Rule of 40 affect the Capital Reinvestment Score?',
    a: 'Two of its components — annual Revenue Growth and Operating Margin trend — are already captured by Revenue CAGR (3-year) and Operating Momentum, both of which feed the Reinvestment Score directly. Adding Rule of 40 to the composite would double-count the same data. Displaying it as a standalone metric gives you the SaaS-industry convention number that institutional analysts cite, without inflating the headline score. Best practice: treat it as a complementary lens, not a substitute.',
  },
];

export default function LearnRuleOf40() {
  const jsonLd = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: FAQS.map(({ q, a }) => ({
      '@type': 'Question',
      name: q,
      acceptedAnswer: { '@type': 'Answer', text: a },
    })),
  };

  return (
    <LearnPage
      title="Rule of 40 — The One-Line Efficiency Check for Growth Stocks | TwylightCrow"
      description="Revenue growth plus profit margin equals at least 40 — the canonical SaaS / growth-stock health check. DiviDrip computes FCF, Operating and SBC-adjusted FCF variants side-by-side so you can tell stock-comp dilution from capex-heavy stories at a glance. Real live examples included."
      canonical="https://twylightcrow.com/learn/rule-of-40"
      guidePath="/learn/rule-of-40"
      sectionLabel="Valuation &amp; Metrics"
      sectionAnchor="non-div-metrics"
      kicker="Learn · Non-Dividend Stocks"
      h1="The Rule of 40 — Growth + Margin in One Number"
      datePublished="2026-02-28"
      jsonLd={jsonLd}
      related={[
        { to: '/learn/operating-momentum', label: 'Operating Momentum — pair Rule of 40 with the acceleration signal' },
        { to: '/learn/anatomy-of-a-stock-buyback', label: 'The Anatomy of a Stock Buyback — the other dilution check' },
        { to: '/learn/beneish-altman-forensic-safety', label: 'Beneish &amp; Altman — the forensic safety gate' },
        { to: '/glossary#entry-rule-of-40', label: 'Glossary: Rule of 40' },
      ]}
    >
      <p>
        The Rule of 40 is the single most-cited efficiency check in the
        non-dividend universe. It exists because old-school valuation
        metrics like the P/E ratio collapse the moment a company decides
        to plow 100% of its cash back into growth — &ldquo;no earnings, no
        P/E&rdquo; gives you nothing actionable. Rule of 40 sidesteps
        that problem by asking a simpler question: <em>is the business
        scaling efficiently, or is it buying growth with capital it
        isn&rsquo;t generating?</em>
      </p>
      <p>
        The math is a single addition problem, but the interpretation
        rewards close reading. DiviDrip by TwylightCrow computes
        <strong>{' '}three variants{' '}</strong>side by side on the
        Capital Analytics tab — the two standard ones (FCF and
        Operating) plus an <strong>SBC-adjusted FCF</strong> variant
        that subtracts stock-based compensation — so the story behind
        a single headline number stays visible.
      </p>

      <h2>The math</h2>
      <table className="learn-table">
        <thead>
          <tr>
            <th>Variant</th>
            <th>Formula</th>
            <th>What it answers</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td className="font-semibold">Rule of 40 (<AbbrTip term="FCF">FCF</AbbrTip>)</td>
            <td className="font-mono">Revenue Growth % + (Free Cash Flow ÷ Revenue) %</td>
            <td>Is the business generating real cash, or only paper profits?</td>
          </tr>
          <tr>
            <td className="font-semibold">Rule of 40 (Operating)</td>
            <td className="font-mono">Revenue Growth % + (Operating Income ÷ Revenue) %</td>
            <td>Is the underlying business model profitable before <AbbrTip term="CAPEX">capex</AbbrTip> and <AbbrTip term="SBC">SBC</AbbrTip>?</td>
          </tr>
          <tr>
            <td className="font-semibold">Rule of 40 (<AbbrTip term="SBC">SBC</AbbrTip>-adjusted <AbbrTip term="FCF">FCF</AbbrTip>)</td>
            <td className="font-mono">Revenue Growth % + ((Free Cash Flow − Stock-Based Comp) ÷ Revenue) %</td>
            <td>Is the cash still there once you stop counting the shares the company handed out instead of salary?</td>
          </tr>
        </tbody>
      </table>
      <p>
        For all three variants, &ldquo;Revenue Growth&rdquo; is the most
        recent annual year-over-year change, not a multi-year{' '}
        <AbbrTip term="CAGR">CAGR</AbbrTip>. Why?
        Because Rule of 40 is meant to catch <em>current</em>{' '}
        efficiency. A company that grew 60% three years ago and is now
        growing 8% would still look strong on a 3-year CAGR — and that
        would defeat the whole point.
      </p>
      <p>
        Why subtract stock-based compensation at all? Because under{' '}
        <AbbrTip term="GAAP">GAAP</AbbrTip> it is a <em>non-cash</em> expense: it lowers Operating
        Income, but the cash-flow statement adds it straight back, so
        Free Cash Flow never feels it. A company paying a quarter of
        its payroll in shares can therefore post a glowing FCF margin
        while its share count creeps up every year. Subtracting the
        SEC-filed SBC figure again puts that cost back where a
        shareholder experiences it. Worked example: 30% growth with a
        20% FCF margin is a Rule of 40 of 50 — a clean pass. If 15% of
        revenue went out as stock comp, the SBC-adjusted margin is 5%
        and the score is 35 — borderline. Same company, same cash,
        very different answer.
      </p>

      <h2>Reading the tiers</h2>
      <table className="learn-table">
        <thead>
          <tr>
            <th>Score</th>
            <th>Tier</th>
            <th>What it means</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td className="font-mono text-emerald-600">60+</td>
            <td className="font-semibold text-emerald-600">Elite</td>
            <td>Every point of growth matched by elite profitability. Rare. Usually only sustained by dominant compounders with structural moats.</td>
          </tr>
          <tr>
            <td className="font-mono text-emerald-600">40 – 60</td>
            <td className="font-semibold text-emerald-600">Healthy</td>
            <td>The canonical pass. Scaling without burning capital. This is what institutional analysts mean by &ldquo;a Rule of 40 company.&rdquo;</td>
          </tr>
          <tr>
            <td className="font-mono text-amber-600">30 – 40</td>
            <td className="font-semibold text-amber-600">Borderline</td>
            <td>Growth or margin starting to crack. Track quarter-over-quarter trend before adding capital.</td>
          </tr>
          <tr>
            <td className="font-mono text-rose-600">0 – 30</td>
            <td className="font-semibold text-rose-600">Failing</td>
            <td>The company is funding growth with capital it isn&rsquo;t earning. Multiple compression usually follows.</td>
          </tr>
          <tr>
            <td className="font-mono text-rose-600">Negative</td>
            <td className="font-semibold text-rose-600">Burning cash</td>
            <td>Revenue contracting AND margins negative. Existential signal in a high-interest-rate environment.</td>
          </tr>
        </tbody>
      </table>

      <h2>Why we display three variants</h2>
      <p>
        The FCF variant is the strict definition used by SaaS investors,
        Bessemer&rsquo;s &ldquo;State of the Cloud&rdquo; reports, and
        most software-coverage analysts on Wall Street. It catches
        companies that report healthy Operating Income but bleed cash
        through stock-based compensation, deferred-revenue timing, or
        working-capital games. The Operating variant is more generous
        because it adds back depreciation and excludes the
        cash-cost of capex — better for capex-heavy industrials
        (semiconductors, telecom, autos) where heavy property-and-
        equipment spending is structural.
      </p>
      <p>
        The SBC-adjusted FCF variant is the tie-breaker. FCF and
        Operating can disagree for two very different reasons — stock
        compensation or capex — and this third tile tells you which one
        you are looking at: if the SBC-adjusted score collapses toward
        the Operating score, the gap was stock comp; if it stays close
        to the plain FCF score, the gap was capex.
      </p>
      <p>
        The most informative move on any non-dividend stock is to look
        at <em>all three</em> at the same time. The size and direction
        of the gaps tell you the story.
      </p>

      <h3>The classic split-signal cases</h3>
      <table className="learn-table">
        <thead>
          <tr>
            <th>Stock (live as of writing)</th>
            <th>R40 (FCF)</th>
            <th>R40 (Operating)</th>
            <th>R40 (SBC-adj FCF)</th>
            <th>What the gaps reveal</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td className="font-semibold">
              <div className="flex items-center gap-2 flex-wrap">
                <span>CRWD (CrowdStrike)</span>
                <SeeItLive ticker="CRWD" tab="capital-analytics" tone="amber" label="Live &rarr;" />
              </div>
            </td>
            <td className="font-mono text-emerald-600">~49</td>
            <td className="font-mono text-amber-600">~16</td>
            <td className="font-mono text-rose-600">~26</td>
            <td>FCF passes ✅, Operating fails ❌, and the SBC-adjusted tile drops 23 points because stock comp runs about 23% of revenue. The gap is dilution, not capex — per-share economics are weaker than the FCF headline suggests.</td>
          </tr>
          <tr>
            <td className="font-semibold">
              <div className="flex items-center gap-2 flex-wrap">
                <span>AMZN (Amazon)</span>
                <SeeItLive ticker="AMZN" tab="capital-analytics" tone="amber" label="Live &rarr;" />
              </div>
            </td>
            <td className="font-mono text-rose-600">~13</td>
            <td className="font-mono text-rose-600">~24</td>
            <td className="font-mono text-rose-600">~11</td>
            <td>Operating &gt; FCF, and the SBC-adjusted tile sits within 3 points of plain FCF — stock comp is under 3% of revenue, so the gap is heavy AWS / fulfillment capex. The Operating variant is the truer read for a capacity-investment phase.</td>
          </tr>
          <tr>
            <td className="font-semibold">
              <div className="flex items-center gap-2 flex-wrap">
                <span>ADBE (Adobe)</span>
                <SeeItLive ticker="ADBE" tab="capital-analytics" tone="emerald" label="Live &rarr;" />
              </div>
            </td>
            <td className="font-mono text-emerald-600">~52</td>
            <td className="font-mono text-emerald-600">~47</td>
            <td className="font-mono text-emerald-600">~44</td>
            <td>All three pass with tight gaps (SBC about 8% of revenue). Mature compounder fingerprint — real cash and real earnings track each other and the shareholder keeps almost all of it.</td>
          </tr>
          <tr>
            <td className="font-semibold">
              <div className="flex items-center gap-2 flex-wrap">
                <span>PLTR (Palantir)</span>
                <SeeItLive ticker="PLTR" tab="capital-analytics" tone="emerald" label="Live &rarr;" />
              </div>
            </td>
            <td className="font-mono text-emerald-600">~103</td>
            <td className="font-mono text-emerald-600">~88</td>
            <td className="font-mono text-emerald-600">~88</td>
            <td>Elite acceleration phase. Revenue growth around 56% YoY with an FCF margin near 47%; stock comp of about 15% of revenue still leaves the SBC-adjusted score at 88. The kind of reading that justifies a 100x+ forward multiple — for as long as it lasts.</td>
          </tr>
          <tr>
            <td className="font-semibold">
              <div className="flex items-center gap-2 flex-wrap">
                <span>SNOW (Snowflake)</span>
                <SeeItLive ticker="SNOW" tab="capital-analytics" tone="amber" label="Live &rarr;" />
              </div>
            </td>
            <td className="font-mono text-emerald-600">~53</td>
            <td className="font-mono text-rose-600">~−1</td>
            <td className="font-mono text-rose-600">~19</td>
            <td>The dilution trap in one row. A Healthy 53 on plain FCF, but stock comp is about 34% of revenue — the SBC-adjusted FCF margin is actually negative, and the score falls to 19. The plain FCF pass is being paid for with your share count.</td>
          </tr>
        </tbody>
      </table>
      <p className="text-[11px] text-muted-foreground italic">
        Values pulled from the weekly equity-quality run (SBC from each
        company&rsquo;s SEC 10-K). Open the Capital Analytics tab on any
        of these tickers to see the current numbers and the
        year-over-year direction.
      </p>

      <h2>Historical inflections worth dwelling on</h2>

      <h3>Salesforce (CRM) — the original Rule of 40 benchmark</h3>
      <p>
        For most of the 2015–2021 era, Salesforce hovered at or above
        50 on the Rule of 40 (FCF) measure — combining 20%+ annual
        revenue growth with 25%+ FCF margins. Each acquisition
        (MuleSoft, Tableau, Slack) was scrutinized by analysts against
        the question: <em>does this deal keep us above 40?</em> When
        the 2022 macro environment forced cost-cutting, Salesforce
        deliberately throttled hiring to preserve the Rule of 40 score
        rather than chase growth. The stock recovered roughly 100% from
        its January 2023 low to its 2024 high, a textbook case of
        Rule-of-40 discipline being rewarded by the market.
      </p>

      <h3>Twilio (TWLO) — the deceleration trap</h3>
      <p>
        Twilio peaked near $450 in February 2021 with revenue growth
        above 60% YoY but persistent negative FCF margins. The market
        was willing to pay a premium for the growth half of the
        equation. As growth decelerated to roughly 9% by late 2022 and
        FCF margin remained near zero, the Rule of 40 score collapsed
        from above 60 to below 15. The stock fell roughly 90% from
        peak to trough, bottoming around $45 in mid-2023. Investors
        watching only revenue growth missed the trend, but investors
        watching Rule of 40 caught the rollover quarters earlier.
      </p>

      <h3>Snowflake (SNOW) — the decelerator that stayed efficient</h3>
      <p>
        Snowflake hit Rule of 40 scores above 110 in fiscal 2021–2022
        on the back of 120%+ revenue growth and slightly negative FCF
        margins. As growth decelerated to roughly 26% by fiscal 2025,
        FCF margins improved to above 25%, keeping the total above 50
        — still a clean Healthy-tier pass. But the market still
        compressed the multiple by roughly 50% from the February 2024
        peak because the <em>delta</em> mattered to growth investors.
        Lesson: a stable Rule of 40 score doesn&rsquo;t guarantee
        multiple stability when the growth-half decelerates sharply.
        Pair Rule of 40 with{' '}
        <Link to="/learn/operating-momentum">Operating Momentum</Link>{' '}
        to catch this.
      </p>

      <h2>The 60-second Rule of 40 check</h2>
      <ol>
        <li>
          Open the stock modal and select the Capital Analytics tab.
          The Rule of 40 card sits just below Buyback Effectiveness and
          shows three tiles: FCF, Operating, and SBC-adjusted FCF. Each
          tile prints its growth and margin terms underneath so you can
          see which half is doing the work.
        </li>
        <li>
          Read the tiles. Is any variant clearly in green (40+)?
          That&rsquo;s the canonical pass. If the card says
          <em>Not meaningful</em> instead, the revenue base is too small
          or too lumpy to score — read the reason and move on.
        </li>
        <li>
          Note the <strong>gaps</strong>. FCF well above Operating with
          the SBC-adjusted tile falling toward Operating (CRWD, SNOW) is
          a dilution signal. Operating above FCF with the SBC-adjusted
          tile hugging plain FCF (AMZN) is a capex signal. All three
          tight together (ADBE) is the mature-compounder fingerprint.
        </li>
        <li>
          Want the whole market at once? The Screener&rsquo;s Growth
          panel filters on all three variants — set{' '}
          <em>Rule of 40 (SBC-adjusted FCF) ≥ 40</em> to see only the
          companies that still pass after stock comp, or combine plain
          FCF ≥ 40 with the SBC-adjusted tile to surface the names that
          pass only before it.
        </li>
        <li>
          Cross-reference with{' '}
          <Link to="/learn/operating-momentum">Operating Momentum</Link>.
          A stable Rule of 40 with negative Operating Momentum (Twilio
          2022) is the classic deceleration trap — the multiple will
          compress even though the canonical pass holds.
        </li>
      </ol>

      <h2>Where Rule of 40 fits in the bigger picture</h2>
      <p>
        Rule of 40 is a <strong>standalone metric</strong> on the
        Capital Analytics tab — it does not feed the Capital
        Reinvestment Score. That&rsquo;s intentional: Revenue CAGR
        (3-year) and Operating Momentum already capture the two
        components from different angles, and double-counting them
        would inflate the composite score. Treat Rule of 40 as the
        industry-convention number you cite when talking to other
        investors, and treat the Capital Reinvestment Score as
        DiviDrip&rsquo;s proprietary composite that includes the
        forensic gates (Beneish, Altman) and shareholder-yield
        components Rule of 40 ignores.
      </p>

      <h2>FAQ</h2>
      <dl className="learn-faq">
        {FAQS.map(({ q, a }, i) => (
          <div key={i} className="my-4">
            <dt className="font-semibold text-foreground">{q}</dt>
            <dd className="mt-1 text-foreground/85">{a}</dd>
          </div>
        ))}
      </dl>

      <h2>Try it</h2>
      <p>
        Open any non-dividend ticker on the{' '}
        <Link to="/dashboard">Dashboard</Link> and select the Capital
        Analytics tab. Compare the three Rule of 40 tiles for a mature
        compounder (ADBE), heavy-SBC software names (CRWD, SNOW), a
        capex-heavy capacity-investment story (AMZN), and a peak
        accelerator (PLTR). The shape of the gaps between the tiles
        tells you the business model story in two seconds.
      </p>

      <p className="mt-6">
        For the full math, tier table, and SeeAlso cross-references,
        see the{' '}
        <AbbrTip term="RULE_OF_40">Rule of 40 glossary entry</AbbrTip>.
      </p>
      <p className="text-xs text-muted-foreground italic">
        This guide is educational. Rule of 40 is a directional check,
        not a guarantee — pair with forensic safety metrics and the
        latest 10-Q before committing capital based on a single number.
      </p>
    </LearnPage>
  );
}
